import React, { useState, useEffect, useMemo } from 'react';
import Card from './ui/Card';
import { getYearStructure, saveYearStructure } from '../services/apiService';
import { HelpIcon } from './Documentation';
import AcademicYearService from '../services/AcademicYearService';

const YearStructureEditor = ({ user }) => {
    const [yearStructure, setYearStructure] = useState({
        academicYear: '',
        startWeek: 1,
        endWeek: 52,
        periods: [],
        holidays: []
    });

    const [selectedYear, setSelectedYear] = useState('2025-2026');

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');

    const [newPeriod, setNewPeriod] = useState({
        name: '',
        startWeek: 1,
        endWeek: 1,
        color: '#3B82F6'
    });

    const [newHoliday, setNewHoliday] = useState({
        name: '',
        startDate: '',
        endDate: '',
        type: 'holiday'
    });

    // New state for holiday auto-find feature
    const [showHolidayModal, setShowHolidayModal] = useState(false);
    const [showCountryModal, setShowCountryModal] = useState(false);
    const [fetchedHolidays, setFetchedHolidays] = useState([]);
    const [selectedHolidays, setSelectedHolidays] = useState(new Set());
    const [fetchingHolidays, setFetchingHolidays] = useState(false);
    const [holidayCountry, setHolidayCountry] = useState('');
    const [detectedCountry, setDetectedCountry] = useState('');

    // Add state for start week tooltip
    const [showStartWeekTooltip, setShowStartWeekTooltip] = useState(false);

    // Comprehensive list of countries supported by Nager.Date API
    const supportedCountries = [
        { code: 'AD', name: 'Andorra' },
        { code: 'AR', name: 'Argentina' },
        { code: 'AT', name: 'Austria' },
        { code: 'AU', name: 'Australia' },
        { code: 'AX', name: 'Åland Islands' },
        { code: 'BA', name: 'Bosnia and Herzegovina' },
        { code: 'BB', name: 'Barbados' },
        { code: 'BE', name: 'Belgium' },
        { code: 'BG', name: 'Bulgaria' },
        { code: 'BJ', name: 'Benin' },
        { code: 'BO', name: 'Bolivia' },
        { code: 'BR', name: 'Brazil' },
        { code: 'BS', name: 'Bahamas' },
        { code: 'BW', name: 'Botswana' },
        { code: 'BY', name: 'Belarus' },
        { code: 'BZ', name: 'Belize' },
        { code: 'CA', name: 'Canada' },
        { code: 'CH', name: 'Switzerland' },
        { code: 'CL', name: 'Chile' },
        { code: 'CN', name: 'China' },
        { code: 'CO', name: 'Colombia' },
        { code: 'CR', name: 'Costa Rica' },
        { code: 'CU', name: 'Cuba' },
        { code: 'CY', name: 'Cyprus' },
        { code: 'CZ', name: 'Czech Republic' },
        { code: 'DE', name: 'Germany' },
        { code: 'DK', name: 'Denmark' },
        { code: 'DO', name: 'Dominican Republic' },
        { code: 'EC', name: 'Ecuador' },
        { code: 'EE', name: 'Estonia' },
        { code: 'EG', name: 'Egypt' },
        { code: 'ES', name: 'Spain' },
        { code: 'FI', name: 'Finland' },
        { code: 'FO', name: 'Faroe Islands' },
        { code: 'FR', name: 'France' },
        { code: 'GA', name: 'Gabon' },
        { code: 'GB', name: 'United Kingdom' },
        { code: 'GD', name: 'Grenada' },
        { code: 'GG', name: 'Guernsey' },
        { code: 'GI', name: 'Gibraltar' },
        { code: 'GL', name: 'Greenland' },
        { code: 'GM', name: 'Gambia' },
        { code: 'GR', name: 'Greece' },
        { code: 'GT', name: 'Guatemala' },
        { code: 'GY', name: 'Guyana' },
        { code: 'HN', name: 'Honduras' },
        { code: 'HR', name: 'Croatia' },
        { code: 'HT', name: 'Haiti' },
        { code: 'HU', name: 'Hungary' },
        { code: 'ID', name: 'Indonesia' },
        { code: 'IE', name: 'Ireland' },
        { code: 'IM', name: 'Isle of Man' },
        { code: 'IS', name: 'Iceland' },
        { code: 'IT', name: 'Italy' },
        { code: 'JE', name: 'Jersey' },
        { code: 'JM', name: 'Jamaica' },
        { code: 'JP', name: 'Japan' },
        { code: 'KR', name: 'South Korea' },
        { code: 'LI', name: 'Liechtenstein' },
        { code: 'LS', name: 'Lesotho' },
        { code: 'LT', name: 'Lithuania' },
        { code: 'LU', name: 'Luxembourg' },
        { code: 'LV', name: 'Latvia' },
        { code: 'MA', name: 'Morocco' },
        { code: 'MC', name: 'Monaco' },
        { code: 'MD', name: 'Moldova' },
        { code: 'MG', name: 'Madagascar' },
        { code: 'MK', name: 'North Macedonia' },
        { code: 'MN', name: 'Mongolia' },
        { code: 'MS', name: 'Montserrat' },
        { code: 'MT', name: 'Malta' },
        { code: 'MW', name: 'Malawi' },
        { code: 'MX', name: 'Mexico' },
        { code: 'MZ', name: 'Mozambique' },
        { code: 'NA', name: 'Namibia' },
        { code: 'NE', name: 'Niger' },
        { code: 'NG', name: 'Nigeria' },
        { code: 'NI', name: 'Nicaragua' },
        { code: 'NL', name: 'Netherlands' },
        { code: 'NO', name: 'Norway' },
        { code: 'NZ', name: 'New Zealand' },
        { code: 'PA', name: 'Panama' },
        { code: 'PE', name: 'Peru' },
        { code: 'PL', name: 'Poland' },
        { code: 'PR', name: 'Puerto Rico' },
        { code: 'PT', name: 'Portugal' },
        { code: 'PY', name: 'Paraguay' },
        { code: 'RO', name: 'Romania' },
        { code: 'RS', name: 'Serbia' },
        { code: 'RU', name: 'Russia' },
        { code: 'SE', name: 'Sweden' },
        { code: 'SG', name: 'Singapore' },
        { code: 'SI', name: 'Slovenia' },
        { code: 'SJ', name: 'Svalbard and Jan Mayen' },
        { code: 'SK', name: 'Slovakia' },
        { code: 'SM', name: 'San Marino' },
        { code: 'SR', name: 'Suriname' },
        { code: 'SV', name: 'El Salvador' },
        { code: 'TN', name: 'Tunisia' },
        { code: 'TR', name: 'Turkey' },
        { code: 'UA', name: 'Ukraine' },
        { code: 'US', name: 'United States' },
        { code: 'UY', name: 'Uruguay' },
        { code: 'VA', name: 'Vatican City' },
        { code: 'VE', name: 'Venezuela' },
        { code: 'VN', name: 'Vietnam' },
        { code: 'ZA', name: 'South Africa' },
        { code: 'ZW', name: 'Zimbabwe' }
    ];

    // Generate available years (2025-2030)
    const getAvailableYears = () => {
        const years = [];
        for (let year = 2025; year <= 2030; year++) {
            years.push(`${year}-${year + 1}`);
        }
        return years;
    };

    // Utility function to get the date of a specific week in a year
    const getDateFromWeek = (academicYear, week) => {
        const startYear = parseInt(academicYear.split('-')[0]);
        const endYear = startYear + 1;

        // Create a date for January 1st of the start year
        const firstJan = new Date(startYear, 0, 1);

        // Get the day of week for January 1st (0 = Sunday, 1 = Monday, etc.)
        const firstJanDay = firstJan.getDay();

        // Calculate days to add to get to the first day (Monday) of week 1
        const daysToFirstMonday = (8 - firstJanDay) % 7;

        // Calculate the date of the Monday of the requested week
        const daysToAdd = daysToFirstMonday + (week - 1) * 7;
        const targetDate = new Date(startYear, 0, 1 + daysToAdd);

        // If the week is in the next year (after week 52), adjust the year
        if (week <= 53 && targetDate.getFullYear() > startYear) {
            // Recalculate using the next year as base
            const nextFirstJan = new Date(endYear, 0, 1);
            const nextFirstJanDay = nextFirstJan.getDay();
            const nextDaysToFirstMonday = (8 - nextFirstJanDay) % 7;
            const nextDaysToAdd = nextDaysToFirstMonday + (week - 1) * 7;
            return new Date(endYear, 0, 1 + nextDaysToAdd).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        }

        return targetDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    // Helper function to validate week within academic year range
    const isWeekWithinAcademicYear = (week, academicStartWeek, academicEndWeek) => {
        // Handle normal academic year (e.g., start_week=1, end_week=52)
        if (academicStartWeek <= academicEndWeek) {
            return week >= academicStartWeek && week <= academicEndWeek;
        }

        // Handle cross-year academic year (e.g., start_week=36, end_week=35)
        // This means weeks 36-53 and weeks 1-35 are valid
        return week >= academicStartWeek || week <= academicEndWeek;
    };

    // Generate week options for period input, filtered by academic year bounds
    const generatePeriodWeekOptions = () => {
        const options = [];
        const currentYear = yearStructure.academicYear || selectedYear;
        const academicStartWeek = yearStructure.startWeek || 1;
        const academicEndWeek = yearStructure.endWeek || 52;

        // Handle cross-year academic years
        if (academicStartWeek <= academicEndWeek) {
            // Normal academic year - weeks in sequence
            for (let week = academicStartWeek; week <= academicEndWeek; week++) {
                const startDate = getDateFromWeek(currentYear, week);
                options.push({
                    value: week,
                    label: `Week ${week} (${startDate})`
                });
            }
        } else {
            // Cross-year academic year - weeks from start to 53, then 1 to end
            for (let week = academicStartWeek; week <= 53; week++) {
                const startDate = getDateFromWeek(currentYear, week);
                options.push({
                    value: week,
                    label: `Week ${week} (${startDate})`
                });
            }
            for (let week = 1; week <= academicEndWeek; week++) {
                const nextYear = `${parseInt(currentYear.split('-')[0]) + 1}-${parseInt(currentYear.split('-')[0]) + 2}`;
                const startDate = getDateFromWeek(nextYear, week);
                options.push({
                    value: week,
                    label: `Week ${week} (${startDate})`
                });
            }
        }

        return options;
    };

    // Generate start week options (weeks 1-53 of the academic year start)
    const generateStartWeekOptions = () => {
        const options = [];
        const currentYear = yearStructure.academicYear || selectedYear;
        const startYear = parseInt(currentYear.split('-')[0]);

        // All weeks for start week should show the first year
        for (let week = 1; week <= 53; week++) {
            const startDate = getDateFromWeek(currentYear, week);
            options.push({
                value: week,
                label: `Week ${week} (${startDate}) - ${startYear}`
            });
        }

        return options;
    };

    // Generate end week options (from start week + 1 to start week - 1 of next year)
    const generateEndWeekOptions = () => {
        const options = [];
        const currentYear = yearStructure.academicYear || selectedYear;
        const startYear = parseInt(currentYear.split('-')[0]);
        const endYear = startYear + 1;
        const nextAcademicYear = `${endYear}-${endYear + 1}`;

        // Add weeks from start week to week 53 of the start year
        for (let week = yearStructure.startWeek; week <= 53; week++) {
            const startDate = getDateFromWeek(currentYear, week);
            options.push({
                value: week,
                label: `Week ${week} (${startDate}) - ${startYear}`
            });
        }

        // Add weeks 1 to (start week - 1) of the next year
        for (let week = 1; week < yearStructure.startWeek; week++) {
            const startDate = getDateFromWeek(nextAcademicYear, week);
            options.push({
                value: week,
                label: `Week ${week} (${startDate}) - ${endYear}`
            });
        }

        return options;
    };

    // Load year structure when component mounts
    useEffect(() => {
        loadYearStructure(selectedYear);
    }, [selectedYear]); // Only depend on selectedYear, not first mount

    const loadYearStructure = async (year) => {
        try {
            setLoading(true);
            setError(null);

            // Try to fetch existing year structure for the specific year
            // We'll pass the year as a query parameter to get year-specific data
            const data = await getYearStructure();

            // Filter or find data for the specific year if multiple years are returned
            // For now, if the returned data matches our year, use it
            if (data && (data.academicYear === year || data.length === 0)) {
                setYearStructure({
                    academicYear: year,
                    startWeek: data.startWeek || 1,
                    endWeek: data.endWeek || 52,
                    periods: data.periods || [],
                    holidays: data.holidays || []
                });
            } else {
                // No existing data for this year, start with empty structure
                setYearStructure({
                    academicYear: year,
                    startWeek: 1,
                    endWeek: 52,
                    periods: [],
                    holidays: []
                });
            }
        } catch (err) {
            if (err.status === 404) {
                // Year doesn't exist, create empty structure
                setYearStructure({
                    academicYear: year,
                    startWeek: 1,
                    endWeek: 52,
                    periods: [],
                    holidays: []
                });
            } else {
                console.error('Error loading year structure:', err);
                setError('Failed to load year structure: ' + err.message);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleYearChange = async (year) => {
        setSelectedYear(year);
        await loadYearStructure(year);
    };

    const fetchYearStructureData = async (academicYearId = null) => {
        // This function is kept for compatibility but simplified
        await loadYearStructure(selectedYear);
    };

    const saveYearStructureData = async () => {
        try {
            setSaving(true);
            setError(null);
            setSuccessMessage('');

            // Validate required fields
            if (!yearStructure.academicYear) {
                throw new Error('Academic year is required');
            }

            const data = await saveYearStructure({
                academicYear: yearStructure.academicYear,
                startWeek: yearStructure.startWeek,
                endWeek: yearStructure.endWeek,
                periods: yearStructure.periods.map(period => ({
                    name: period.name,
                    startWeek: period.startWeek,
                    endWeek: period.endWeek,
                    color: period.color
                })),
                holidays: yearStructure.holidays.map(holiday => ({
                    name: holiday.name,
                    startDate: holiday.startDate,
                    endDate: holiday.endDate,
                    type: holiday.type,
                    description: holiday.description || null
                }))
            });

            setSuccessMessage('Year structure saved successfully!');

            // Update with server response to get IDs
            if (data.yearStructure) {
                setYearStructure(prev => ({
                    ...prev,
                    periods: data.yearStructure.periods || prev.periods,
                    holidays: data.yearStructure.holidays || prev.holidays
                }));
            }

            // Clear AcademicYearService cache so conflict detection picks up any holiday changes
            const academicYearService = AcademicYearService.getInstance();
            academicYearService.clearCache();
            console.log('[YearStructureEditor] Cleared AcademicYearService cache after saving year structure');

            // Clear success message after 3 seconds
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            console.error('Error saving year structure:', err);
            setError('Failed to save year structure: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    const addPeriod = async () => {
        if (newPeriod.name && newPeriod.startWeek && newPeriod.endWeek) {
            try {
                setSaving(true);
                setError(null);

                const period = {
                    ...newPeriod,
                    id: 'temp_' + Date.now() // Temporary ID until saved
                };

                // Add to local state first
                const updatedYearStructure = {
                    ...yearStructure,
                    periods: [...yearStructure.periods, period]
                };

                setYearStructure(updatedYearStructure);

                // Save to backend immediately
                const data = await saveYearStructure({
                    academicYear: updatedYearStructure.academicYear,
                    startWeek: updatedYearStructure.startWeek,
                    endWeek: updatedYearStructure.endWeek,
                    periods: updatedYearStructure.periods.map(p => ({
                        name: p.name,
                        startWeek: p.startWeek,
                        endWeek: p.endWeek,
                        color: p.color
                    })),
                    holidays: updatedYearStructure.holidays.map(holiday => ({
                        name: holiday.name,
                        startDate: holiday.startDate,
                        endDate: holiday.endDate,
                        type: holiday.type,
                        description: holiday.description || null
                    }))
                });

                // Update with server response to get real IDs
                if (data.yearStructure) {
                    setYearStructure(prev => ({
                        ...prev,
                        periods: data.yearStructure.periods || prev.periods,
                        holidays: data.yearStructure.holidays || prev.holidays
                    }));
                }

                setSuccessMessage(`Period "${newPeriod.name}" added successfully!`);

                // Clear the form
                setNewPeriod({
                    name: '',
                    startWeek: 1,
                    endWeek: 1,
                    color: '#3B82F6'
                });

                // Clear success message after 3 seconds
                setTimeout(() => setSuccessMessage(''), 3000);
            } catch (err) {
                console.error('Error saving period:', err);
                setError('Failed to save period: ' + err.message);
                // Revert the local state change on error
                setYearStructure(yearStructure);
            } finally {
                setSaving(false);
            }
        }
    };

    const removePeriod = (id) => {
        setYearStructure(prev => ({
            ...prev,
            periods: prev.periods.filter(p => p.id !== id)
        }));
    };

    const addHoliday = async () => {
        if (newHoliday.name && newHoliday.startDate && newHoliday.endDate) {
            try {
                setSaving(true);
                setError(null);

                // Ensure the type is one of the allowed values
                const validTypes = ['holiday', 'break', 'vacation', 'other'];
                const type = validTypes.includes(newHoliday.type) ? newHoliday.type : 'holiday';

                const holiday = {
                    ...newHoliday,
                    type, // Use the validated type
                    id: 'temp_' + Date.now() // Temporary ID until saved
                };

                // Add to local state first
                const updatedYearStructure = {
                    ...yearStructure,
                    holidays: [...yearStructure.holidays, holiday]
                };

                setYearStructure(updatedYearStructure);

                // Save to backend immediately
                const data = await saveYearStructure({
                    academicYear: updatedYearStructure.academicYear,
                    startWeek: updatedYearStructure.startWeek,
                    endWeek: updatedYearStructure.endWeek,
                    periods: updatedYearStructure.periods.map(period => ({
                        name: period.name,
                        startWeek: period.startWeek,
                        endWeek: period.endWeek,
                        color: period.color
                    })),
                    holidays: updatedYearStructure.holidays.map(h => ({
                        name: h.name,
                        startDate: h.startDate,
                        endDate: h.endDate,
                        type: validTypes.includes(h.type) ? h.type : 'holiday', // Ensure type is valid for all holidays
                        description: h.description || null
                    }))
                });

                // Update with server response to get real IDs
                if (data.yearStructure) {
                    setYearStructure(prev => ({
                        ...prev,
                        periods: data.yearStructure.periods || prev.periods,
                        holidays: data.yearStructure.holidays || prev.holidays
                    }));
                }

                setSuccessMessage(`Holiday "${newHoliday.name}" added successfully!`);

                // Clear AcademicYearService cache so conflict detection picks up the new holiday
                const academicYearService = AcademicYearService.getInstance();
                academicYearService.clearCache();
                console.log('[YearStructureEditor] Cleared AcademicYearService cache after adding holiday');

                // Clear the form
                setNewHoliday({
                    name: '',
                    startDate: '',
                    endDate: '',
                    type: 'holiday'
                });

                // Clear success message after 3 seconds
                setTimeout(() => setSuccessMessage(''), 3000);
            } catch (err) {
                console.error('Error saving holiday:', err);
                setError('Failed to save holiday: ' + err.message);
                // Revert the local state change on error
                setYearStructure(yearStructure);
            } finally {
                setSaving(false);
            }
        }
    };

    const removeHoliday = (id) => {
        setYearStructure(prev => ({
            ...prev,
            holidays: prev.holidays.filter(h => h.id !== id)
        }));

        // Clear AcademicYearService cache so conflict detection updates
        const academicYearService = AcademicYearService.getInstance();
        academicYearService.clearCache();
        console.log('[YearStructureEditor] Cleared AcademicYearService cache after removing holiday');
    };

    const updateYearStructure = (field, value) => {
        setYearStructure(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Function to detect user's location and get country code
    const detectUserLocation = async () => {
        try {
            // Method 1: Try IP geolocation first (most accurate)
            try {
                const response = await fetch('https://ipapi.co/json/');
                const data = await response.json();
                if (data.country_code) {
                    return data.country_code;
                }
            } catch (ipError) {
                console.log('IP geolocation failed, trying timezone method');
            }

            // Method 2: Fallback to timezone detection
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const timezoneToCountry = {
                'America/New_York': 'US',
                'America/Los_Angeles': 'US',
                'America/Chicago': 'US',
                'America/Denver': 'US',
                'America/Toronto': 'CA',
                'America/Vancouver': 'CA',
                'Europe/London': 'GB',
                'Europe/Paris': 'FR',
                'Europe/Berlin': 'DE',
                'Europe/Rome': 'IT',
                'Europe/Madrid': 'ES',
                'Europe/Amsterdam': 'NL',
                'Europe/Brussels': 'BE',
                'Europe/Vienna': 'AT',
                'Europe/Zurich': 'CH',
                'Europe/Stockholm': 'SE',
                'Europe/Copenhagen': 'DK',
                'Europe/Oslo': 'NO',
                'Europe/Helsinki': 'FI',
                'Europe/Warsaw': 'PL',
                'Europe/Prague': 'CZ',
                'Europe/Budapest': 'HU',
                'Europe/Bucharest': 'RO',
                'Europe/Sofia': 'BG',
                'Europe/Athens': 'GR',
                'Europe/Dublin': 'IE',
                'Europe/Lisbon': 'PT',
                'Asia/Tokyo': 'JP',
                'Asia/Seoul': 'KR',
                'Asia/Shanghai': 'CN',
                'Asia/Singapore': 'SG',
                'Asia/Hong_Kong': 'HK',
                'Asia/Taipei': 'TW',
                'Asia/Bangkok': 'TH',
                'Asia/Jakarta': 'ID',
                'Asia/Manila': 'PH',
                'Asia/Kuala_Lumpur': 'MY',
                'Asia/Mumbai': 'IN',
                'Asia/Dubai': 'AE',
                'Australia/Sydney': 'AU',
                'Australia/Melbourne': 'AU',
                'Australia/Perth': 'AU',
                'Pacific/Auckland': 'NZ'
            };

            return timezoneToCountry[timezone] || 'US';
        } catch (error) {
            console.error('Location detection failed:', error);
            return 'US'; // Default fallback
        }
    };

    // Function to fetch holidays from Nager.Date API
    const fetchHolidaysForYear = async (countryCode, year) => {
        try {
            const response = await fetch(`https://date.nager.at/api/v3/publicholidays/${year}/${countryCode}`);
            if (!response.ok) {
                throw new Error(`Failed to fetch holidays: ${response.status}`);
            }
            const holidays = await response.json();

            return holidays.map(holiday => ({
                id: 'auto_' + holiday.date + '_' + holiday.name.replace(/\s+/g, '_'),
                name: holiday.name,
                localName: holiday.localName,
                startDate: holiday.date,
                endDate: holiday.date,
                type: holiday.global ? 'holiday' : 'regional',
                description: holiday.localName !== holiday.name ? holiday.localName : null,
                source: 'auto-detected'
            }));
        } catch (error) {
            console.error('Error fetching holidays:', error);
            throw error;
        }
    };

    // Function to handle auto-find holidays
    const handleAutoFindHolidays = async () => {
        try {
            setFetchingHolidays(true);
            setError(null);

            // Step 1: Detect user location
            const detectedCountryCode = await detectUserLocation();
            setDetectedCountry(detectedCountryCode);

            // Step 2: Show country selection modal
            setShowCountryModal(true);

        } catch (error) {
            console.error('Error in auto-find holidays:', error);
            setError('Failed to detect location: ' + error.message);
        } finally {
            setFetchingHolidays(false);
        }
    };

    // Function to handle country selection and fetch holidays
    const handleCountrySelection = async (countryCode) => {
        try {
            setFetchingHolidays(true);
            setError(null);
            setShowCountryModal(false);
            setHolidayCountry(countryCode);

            // Step 1: Get the academic year range
            const academicYear = yearStructure.academicYear || selectedYear;
            const startYear = parseInt(academicYear.split('-')[0]);
            const endYear = startYear + 1;

            // Step 2: Fetch holidays for both years (since academic year spans two calendar years)
            const [currentYearHolidays, nextYearHolidays] = await Promise.all([
                fetchHolidaysForYear(countryCode, startYear),
                fetchHolidaysForYear(countryCode, endYear)
            ]);

            const allHolidays = [...currentYearHolidays, ...nextYearHolidays];

            // Step 3: Filter holidays that fall within the academic year date range
            const academicStartDate = new Date(startYear, 8, 1); // September 1st (typical academic year start)
            const academicEndDate = new Date(endYear, 7, 31); // August 31st (typical academic year end)

            const relevantHolidays = allHolidays.filter(holiday => {
                const holidayDate = new Date(holiday.startDate);
                return holidayDate >= academicStartDate && holidayDate <= academicEndDate;
            });

            setFetchedHolidays(relevantHolidays);
            setSelectedHolidays(new Set()); // Clear previous selections
            setShowHolidayModal(true);

        } catch (error) {
            console.error('Error in fetching holidays:', error);
            setError('Failed to fetch holidays: ' + error.message);
        } finally {
            setFetchingHolidays(false);
        }
    };

    // Function to format date range display
    const formatDateRange = (startDate, endDate) => {
        const start = new Date(startDate);
        const end = new Date(endDate);

        const formatOptions = {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        };

        if (startDate === endDate) {
            // Single day
            return start.toLocaleDateString('en-US', formatOptions);
        } else {
            // Date range
            const startFormatted = start.toLocaleDateString('en-US', formatOptions);
            const endFormatted = end.toLocaleDateString('en-US', formatOptions);
            return `${startFormatted} - ${endFormatted}`;
        }
    };

    // Function to toggle holiday selection
    const toggleHolidaySelection = (holidayId) => {
        const newSelected = new Set(selectedHolidays);
        if (newSelected.has(holidayId)) {
            newSelected.delete(holidayId);
        } else {
            newSelected.add(holidayId);
        }
        setSelectedHolidays(newSelected);
    };

    // Function to import selected holidays
    const importSelectedHolidays = async () => {
        try {
            setSaving(true);
            setError(null);

            // Filter selected holidays and format them
            const selectedHolidayData = fetchedHolidays
                .filter(h => selectedHolidays.has(h.id))
                .map(h => ({
                    name: h.name,
                    startDate: h.startDate,
                    endDate: h.endDate,
                    type: 'holiday', // Always set type to 'holiday' for auto-found holidays
                    description: h.description || null
                }));

            // Add to local state first
            const updatedYearStructure = {
                ...yearStructure,
                holidays: [...yearStructure.holidays, ...selectedHolidayData]
            };

            // Save to backend
            const data = await saveYearStructure({
                academicYear: updatedYearStructure.academicYear,
                startWeek: updatedYearStructure.startWeek,
                endWeek: updatedYearStructure.endWeek,
                periods: updatedYearStructure.periods.map(period => ({
                    name: period.name,
                    startWeek: period.startWeek,
                    endWeek: period.endWeek,
                    color: period.color
                })),
                holidays: updatedYearStructure.holidays.map(h => ({
                    name: h.name,
                    startDate: h.startDate,
                    endDate: h.endDate,
                    type: 'holiday', // Ensure type is 'holiday' for all imported holidays
                    description: h.description || null
                }))
            });

            // Update with server response
            if (data.yearStructure) {
                setYearStructure(prev => ({
                    ...prev,
                    periods: data.yearStructure.periods || prev.periods,
                    holidays: data.yearStructure.holidays || prev.holidays
                }));
            }

            setSuccessMessage(`Successfully imported ${selectedHolidayData.length} holidays!`);
            setShowHolidayModal(false);
            setSelectedHolidays(new Set());

            // Clear AcademicYearService cache so conflict detection picks up the new holidays
            const academicYearService = AcademicYearService.getInstance();
            academicYearService.clearCache();
            console.log('[YearStructureEditor] Cleared AcademicYearService cache after importing holidays');

            // Clear success message after 3 seconds
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            console.error('Error importing holidays:', err);
            setError('Failed to import holidays: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center py-12">
                <div className="text-lg text-gray-600">
                    Loading year structure...
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div className="flex items-center space-x-2">
                    <h2 className="text-2xl font-bold text-gray-800">Year Structure Editor</h2>
                    <HelpIcon
                        documentationId="year-structure-editor"
                        position="inline"
                        size={18}
                        variant="info"
                    />
                </div>
                <button
                    onClick={saveYearStructureData}
                    disabled={saving}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {saving ? 'Saving...' : 'Save Changes'}
                </button>
            </div>

            {/* Error/Success Messages */}
            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                    {error}
                </div>
            )}
            {successMessage && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md">
                    {successMessage}
                </div>
            )}

            {/* Academic Year Settings */}
            <Card className="p-6">
                <div className="flex items-center space-x-2 mb-4">
                    <h3 className="text-lg font-semibold text-gray-800">Academic Year Settings</h3>
                    <HelpIcon
                        documentationId="year-structure-academic-year"
                        position="inline"
                        size={16}
                        variant="info"
                    />
                </div>
                <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Select Academic Year
                            </label>
                            <select
                                value={selectedYear}
                                onChange={(e) => handleYearChange(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                {getAvailableYears().map(year => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                                Start Week
                                <HelpIcon
                                    documentationId="year-structure-start-week"
                                    position="inline"
                                    size={14}
                                    variant="info"
                                />
                            </label>
                            <div
                                className="relative"
                                onMouseEnter={() => setShowStartWeekTooltip(true)}
                                onMouseLeave={() => setShowStartWeekTooltip(false)}
                            >
                                <select
                                    value={yearStructure.startWeek}
                                    onChange={(e) => updateYearStructure('startWeek', parseInt(e.target.value))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                // disabled={false}
                                >
                                    {generateStartWeekOptions().map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                                {showStartWeekTooltip && (
                                    <div className="absolute z-10 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-sm tooltip -top-12 left-1/2 transform -translate-x-1/2">
                                        Coming soon
                                        <div className="tooltip-arrow absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                                End Week
                                <HelpIcon
                                    documentationId="year-structure-end-week"
                                    position="inline"
                                    size={14}
                                    variant="info"
                                />
                            </label>
                            <select
                                value={yearStructure.endWeek}
                                onChange={(e) => updateYearStructure('endWeek', parseInt(e.target.value))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                disabled={!yearStructure.academicYear}
                            >
                                {generateEndWeekOptions().map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
            </Card>

            {/* Academic Periods */}
            <Card className="p-6">
                <div className="flex items-center space-x-2 mb-4">
                    <h3 className="text-lg font-semibold text-gray-800">Academic Periods</h3>
                    <HelpIcon
                        documentationId="year-structure-periods"
                        position="inline"
                        size={16}
                        variant="info"
                    />
                </div>

                {/* Add New Period */}
                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                    <h4 className="text-md font-medium text-gray-700 mb-3">Add New Period</h4>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <input
                            type="text"
                            placeholder="Period name"
                            value={newPeriod.name}
                            onChange={(e) => setNewPeriod(prev => ({ ...prev, name: e.target.value }))}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <select
                            value={newPeriod.startWeek}
                            onChange={(e) => setNewPeriod(prev => ({ ...prev, startWeek: parseInt(e.target.value) }))}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            disabled={!yearStructure.academicYear}
                        >
                            {generatePeriodWeekOptions().map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        <select
                            value={newPeriod.endWeek}
                            onChange={(e) => setNewPeriod(prev => ({ ...prev, endWeek: parseInt(e.target.value) }))}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            disabled={!yearStructure.academicYear}
                        >
                            {generatePeriodWeekOptions().map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        <div className="flex gap-2">
                            <input
                                type="color"
                                value={newPeriod.color}
                                onChange={(e) => setNewPeriod(prev => ({ ...prev, color: e.target.value }))}
                                className="w-12 h-10 border border-gray-300 rounded-md"
                            />
                            <button
                                onClick={addPeriod}
                                disabled={saving}
                                className="flex-1 px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {saving ? 'Saving...' : 'Add'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Existing Periods */}
                <div className="space-y-3">
                    {yearStructure.periods.length === 0 ? (
                        <p className="text-gray-500 text-center py-4">No periods defined yet. Add your first period above.</p>
                    ) : (
                        yearStructure.periods.map(period => (
                            <div key={period.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                                <div className="flex items-center space-x-4">
                                    <div
                                        className="w-4 h-4 rounded"
                                        style={{ backgroundColor: period.color }}
                                    ></div>
                                    <div>
                                        <span className="font-medium text-gray-800">{period.name}</span>
                                        <span className="text-sm text-gray-600 ml-2">
                                            Week {period.startWeek} to Week {period.endWeek}
                                        </span>
                                    </div>
                                </div>
                                <button
                                    onClick={() => removePeriod(period.id)}
                                    className="px-3 py-1 bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
                                >
                                    Remove
                                </button>
                            </div>
                        ))
                    )}
                </div>
            </Card>

            {/* Holidays and Breaks */}
            <Card className="p-6">
                <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center space-x-2">
                        <h3 className="text-lg font-semibold text-gray-800">Holidays and Breaks</h3>
                        <HelpIcon
                            documentationId="year-structure-holidays"
                            position="inline"
                            size={16}
                            variant="info"
                        />
                    </div>
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={handleAutoFindHolidays}
                            disabled={fetchingHolidays || !yearStructure.academicYear}
                            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {fetchingHolidays ? 'Finding...' : 'Auto Find'}
                        </button>
                        <HelpIcon
                            documentationId="year-structure-auto-holidays"
                            position="inline"
                            size={16}
                            variant="info"
                        />
                    </div>
                </div>

                {/* Add New Holiday */}
                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                    <h4 className="text-md font-medium text-gray-700 mb-3">Add New Holiday/Break</h4>
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                        <input
                            type="text"
                            placeholder="Holiday name"
                            value={newHoliday.name}
                            onChange={(e) => setNewHoliday(prev => ({ ...prev, name: e.target.value }))}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <input
                            type="date"
                            value={newHoliday.startDate}
                            onChange={(e) => setNewHoliday(prev => ({ ...prev, startDate: e.target.value }))}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <input
                            type="date"
                            value={newHoliday.endDate}
                            onChange={(e) => setNewHoliday(prev => ({ ...prev, endDate: e.target.value }))}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <select
                            value={newHoliday.type}
                            onChange={(e) => setNewHoliday(prev => ({ ...prev, type: e.target.value }))}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="holiday">Holiday</option>
                            <option value="break">Break</option>
                            <option value="vacation">Vacation</option>
                            <option value="other">Other</option>
                        </select>
                        <button
                            onClick={addHoliday}
                            disabled={saving}
                            className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {saving ? 'Saving...' : 'Add Holiday'}
                        </button>
                    </div>
                </div>

                {/* Existing Holidays */}
                <div className="space-y-3">
                    {yearStructure.holidays.length === 0 ? (
                        <p className="text-gray-500 text-center py-4">No holidays defined yet. Add your first holiday above.</p>
                    ) : (
                        yearStructure.holidays.map(holiday => (
                            <div key={holiday.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                                <div className="flex items-center space-x-4">
                                    <div className="w-4 h-4 rounded bg-red-500"></div>
                                    <div>
                                        <span className="font-medium text-gray-800">{holiday.name}</span>
                                        <span className="text-sm text-gray-600 ml-2">
                                            {holiday.startDate} to {holiday.endDate}
                                        </span>
                                        <span className="text-xs text-gray-500 ml-2 capitalize">
                                            ({holiday.type})
                                        </span>
                                    </div>
                                </div>
                                <button
                                    onClick={() => removeHoliday(holiday.id)}
                                    className="px-3 py-1 bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
                                >
                                    Remove
                                </button>
                            </div>
                        ))
                    )}
                </div>
            </Card>

            {/* Country Selection Modal */}
            {showCountryModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-2xl max-h-[80vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-semibold text-gray-800">
                                Select Country for Holidays
                            </h3>
                            <button
                                onClick={() => {
                                    setShowCountryModal(false);
                                    setFetchingHolidays(false);
                                }}
                                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                            >
                                ×
                            </button>
                        </div>

                        <div className="mb-4">
                            <p className="text-sm text-gray-600 mb-2">
                                {detectedCountry ? (
                                    <>We detected your location as <strong>{supportedCountries.find(c => c.code === detectedCountry)?.name || detectedCountry}</strong>. Please confirm or select a different country:</>
                                ) : (
                                    'Please select your country to find relevant holidays:'
                                )}
                            </p>
                        </div>

                        <div className="space-y-2 max-h-96 overflow-y-auto">
                            {supportedCountries
                                .sort((a, b) => {
                                    // Put detected country first, then alphabetical order
                                    if (a.code === detectedCountry) return -1;
                                    if (b.code === detectedCountry) return 1;
                                    return a.name.localeCompare(b.name);
                                })
                                .map(country => (
                                    <div
                                        key={country.code}
                                        className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors ${country.code === detectedCountry
                                            ? 'border-purple-500 bg-purple-50'
                                            : 'border-gray-200 hover:bg-gray-50'
                                            }`}
                                        onClick={() => handleCountrySelection(country.code)}
                                    >
                                        <div className="flex items-center">
                                            <span className="font-medium text-gray-800">{country.name}</span>
                                            <span className="text-sm text-gray-600 ml-2">({country.code})</span>
                                            {country.code === detectedCountry && (
                                                <span className="ml-2 px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">
                                                    Detected
                                                </span>
                                            )}
                                        </div>
                                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                                        </svg>
                                    </div>
                                ))}
                        </div>

                        <div className="flex justify-end pt-4 border-t mt-4">
                            <button
                                onClick={() => {
                                    setShowCountryModal(false);
                                    setFetchingHolidays(false);
                                }}
                                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Holiday Selection Modal */}
            {showHolidayModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-4xl max-h-[80vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-semibold text-gray-800">
                                Select Holidays to Import ({supportedCountries.find(c => c.code === holidayCountry)?.name || holidayCountry})
                            </h3>
                            <button
                                onClick={() => setShowHolidayModal(false)}
                                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                            >
                                ×
                            </button>
                        </div>

                        {fetchedHolidays.length === 0 ? (
                            <div className="text-center py-8">
                                <p className="text-gray-600">No holidays found for {supportedCountries.find(c => c.code === holidayCountry)?.name || holidayCountry} in the academic year {yearStructure.academicYear || selectedYear}.</p>
                            </div>
                        ) : (
                            <>
                                <div className="mb-4">
                                    <p className="text-sm text-gray-600 mb-2">
                                        Found {fetchedHolidays.length} holidays for academic year {yearStructure.academicYear || selectedYear}
                                    </p>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => setSelectedHolidays(new Set(fetchedHolidays.map(h => h.id)))}
                                            className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200"
                                        >
                                            Select All
                                        </button>
                                        <button
                                            onClick={() => setSelectedHolidays(new Set())}
                                            className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200"
                                        >
                                            Clear All
                                        </button>
                                    </div>
                                </div>

                                <div className="space-y-2 max-h-96 overflow-y-auto mb-4">
                                    {fetchedHolidays.map(holiday => (
                                        <div
                                            key={holiday.id}
                                            className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${selectedHolidays.has(holiday.id)
                                                ? 'border-blue-500 bg-blue-50'
                                                : 'border-gray-200 hover:bg-gray-50'
                                                }`}
                                            onClick={() => toggleHolidaySelection(holiday.id)}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedHolidays.has(holiday.id)}
                                                onChange={() => toggleHolidaySelection(holiday.id)}
                                                className="mr-3"
                                            />
                                            <div className="flex-1">
                                                <div className="font-medium text-gray-800">
                                                    {holiday.name}
                                                    {holiday.localName && holiday.localName !== holiday.name && (
                                                        <span className="text-sm text-gray-600 ml-2">({holiday.localName})</span>
                                                    )}
                                                </div>
                                                <div className="text-sm text-gray-600">
                                                    {formatDateRange(holiday.startDate, holiday.endDate)}
                                                    <span className="ml-2 px-2 py-1 bg-gray-100 rounded text-xs capitalize">
                                                        {holiday.type}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="flex justify-between items-center pt-4 border-t">
                                    <div className="text-sm text-gray-600">
                                        {selectedHolidays.size} of {fetchedHolidays.length} holidays selected
                                    </div>
                                    <div className="flex gap-3">
                                        <button
                                            onClick={() => setShowHolidayModal(false)}
                                            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={importSelectedHolidays}
                                            disabled={saving || selectedHolidays.size === 0}
                                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {saving ? 'Importing...' : `Import ${selectedHolidays.size} Holidays`}
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Summary */}
            <Card className="p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Year Structure Summary</h3>
                <div className="text-sm text-gray-600 space-y-2">
                    <p><strong>Academic Year:</strong> {yearStructure.academicYear || 'Not set'}</p>
                    <p><strong>Duration:</strong> Week {yearStructure.startWeek} to Week {yearStructure.endWeek}</p>
                    <p><strong>Year Structure Periods:</strong> {yearStructure.periods.length} defined</p>
                    <p><strong>Holidays/Breaks:</strong> {yearStructure.holidays.length} defined</p>
                </div>
                {yearStructure.periods.length === 0 && yearStructure.holidays.length === 0 && (
                    <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                        <p className="text-yellow-800 text-sm">
                            <strong>Tip:</strong> Add some academic periods (like semesters) and holidays to complete your year structure setup.
                        </p>
                    </div>
                )}
            </Card>
        </div>
    );
};

export default YearStructureEditor; 